export interface WordSearchLevel {
  levelNumber?: number;
  gridSize: number;
  grid: string[];
  words: string[];
  theme?: string;
  wordPositions?: Record<string, string[]>;
  solution?: Record<string, string[]>;
}

export type SearchDirection =
  | 'right'
  | 'down'
  | 'left'
  | 'up'
  | 'downRight'
  | 'downLeft'
  | 'upRight'
  | 'upLeft';

export const DIRECTION_DELTAS: Record<SearchDirection, { dr: number; dc: number }> = {
  right: { dr: 0, dc: 1 },
  down: { dr: 1, dc: 0 },
  left: { dr: 0, dc: -1 },
  up: { dr: -1, dc: 0 },
  downRight: { dr: 1, dc: 1 },
  downLeft: { dr: 1, dc: -1 },
  upRight: { dr: -1, dc: 1 },
  upLeft: { dr: -1, dc: -1 },
};

export interface PlacedWord {
  word: string;
  startRow: number;
  startCol: number;
  direction: SearchDirection;
}

export interface Cell {
  row: number;
  col: number;
}

export function parseLevel(level: WordSearchLevel): { grid: string[][]; words: PlacedWord[] } {
  const grid = level.grid.map((row) => row.toUpperCase().split(''));
  const positions = level.wordPositions ?? level.solution ?? {};
  const placed: PlacedWord[] = [];
  for (const w of level.words) {
    const key = positions[w] ? w : positions[w.toUpperCase()] ? w.toUpperCase() : null;
    if (!key) continue;
    const arr = positions[key];
    if (!arr || arr.length < 2) continue;
    const pts = arr
      .map((s) => s.split(',').map((x) => parseInt(x.trim(), 10)))
      .filter((p) => p.length === 2 && p.every((n) => Number.isFinite(n)));
    if (pts.length < 2) continue;
    const [r0, c0] = pts[0];
    const [r1, c1] = pts[1];
    const dr = r1 - r0;
    const dc = c1 - c0;
    const dir = directionFromDelta(dr, dc);
    if (!dir) continue;
    placed.push({ word: w.toUpperCase(), startRow: r0, startCol: c0, direction: dir });
  }
  return { grid, words: placed };
}

function directionFromDelta(dr: number, dc: number): SearchDirection | null {
  if (dr === 0 && dc === 1) return 'right';
  if (dr === 0 && dc === -1) return 'left';
  if (dr === 1 && dc === 0) return 'down';
  if (dr === -1 && dc === 0) return 'up';
  if (dr === 1 && dc === 1) return 'downRight';
  if (dr === 1 && dc === -1) return 'downLeft';
  if (dr === -1 && dc === 1) return 'upRight';
  if (dr === -1 && dc === -1) return 'upLeft';
  return null;
}

/** Snap arbitrary vector to nearest 45° direction. */
export function snapDirection(fromR: number, fromC: number, toR: number, toC: number): SearchDirection {
  const dr = toR - fromR;
  const dc = toC - fromC;
  const angle = Math.atan2(dr, dc);
  const deg = ((angle * 180) / Math.PI + 360) % 360;
  const snapped = Math.round(deg / 45) % 8;
  return (
    [
      'right',
      'downRight',
      'down',
      'downLeft',
      'left',
      'upLeft',
      'up',
      'upRight',
    ] as SearchDirection[]
  )[snapped];
}
